import { Injectable } from '@angular/core';
import { AssessmentType, GlobalEmailTypes } from '../constants-types/Enums';
import {
    PizzaObjectiveType,
    BlindspotOrStrengthType,
    PizzaConfigType,
    DerivedTeamName,
    derivedTeamStrategyPercentageMap,
    TeamIndividualContributionType,
    TeamDiscoveryActionType,
    TeamDiscoveryActionSubtype,
    TeamManagerType
} from '../constants-types/StrategyConstants';
import { BehavioralFactorNoE } from '../core-data-model/BehavioralFactor.model';
import { MinMax } from '../core-data-model/Common.model';
import { EngageChecklistCategory } from '../core-data-model/Engage.model';
import { RefProfileReportInfo } from '../core-data-model/Job/JobReportInfo.model';
import { ReferencePatternType } from '../core-data-model/ReferencePatternType.model';
import {
    CTAActions,
    CallToActionLocation
} from '../core-services/CallToAction.service';
import { StringUtilities } from '../core-utilities/String.utility';
import { GuideFactorCategory, GuideFactorCondition } from '../shared/Enums';
import { TranslationService } from '../translations';

@Injectable({ providedIn: 'root' })
export class TagMaker {
    constructor(
        private TR: TranslationService,
        private stringUtil: StringUtilities
    ) {}

    appendNumber(key: string, num: number) {
        return `${key}${num}`;
    }

    videoLink(referencePattern: ReferencePatternType) {
        return `portablePersonPage.videoLink.${referencePattern.keyFragment}`;
    }
    videoLinkText(referencePattern: ReferencePatternType) {
        return `portablePersonPage.videoLinkText.${referencePattern.keyFragment}`;
    }

    youAre(referencePattern: ReferencePatternType) {
        return `portablePersonPage.youAre.${referencePattern.keyFragment}`;
    }

    assessmentEmailPillboxLabel(assessmentType: AssessmentType): string {
        return `assessmentCenter.title-${AssessmentType[
            assessmentType
        ].toLowerCase()}-for`;
    }
    pluralizeCommonReferenceProfile(amount: number) {
        return `assessedEmployeeCountsCard.referenceProfilesAddendum-${amount}`;
    }

    referenceProfileName(refProfile: string) {
        return `toolkit-referenceProfile.name-${refProfile}`;
    }

    keyWithDisabledOption(key: string, disabled: boolean) {
        return `${key}${disabled ? '-disabled' : ''}`;
    }

    getFactorKeyword(factor: string): string {
        let factorSuffix = '';
        const factorKey = 'text.common-terms.';

        switch (factor) {
            case 'A':
                factorSuffix = 'dominance';
                break;
            case 'B':
                factorSuffix = 'extraversion';
                break;
            case 'C':
                factorSuffix = 'patience';
                break;
            case 'D':
                factorSuffix = 'formality';
                break;
        }
        return this.TR.key(factorKey.concat(factorSuffix));
    }

    getFactorGraphTitle(factor: string): string {
        return this.TR.key(
            `label.analytics.graphs.factor${factor.toLowerCase()}.title`
        );
    }

    getFactorGraphAverage(factor: string): string {
        return this.TR.key(
            `label.analytics.graphs.factor${factor.toLowerCase()}.average`
        );
    }

    getEngageInsightsCategoryTitle(dimension: string) {
        return this.TR.key(
            `engage-insights-category.${dimension.toLowerCase()}-title`
        );
    }

    getEngageCategoryDescription(dimension: string) {
        return this.TR.key(
            `engage-insights-category.${dimension.toLowerCase()}-description`
        );
    }
    getCTALocationTitle(action: CTAActions, location: CallToActionLocation) {
        return this.TR.key(`cta.${action}-${location}.title`);
    }

    getCTALocationDetail(action: CTAActions, location: CallToActionLocation) {
        return this.TR.key(`cta.${action}-${location}.detail`);
    }

    softwareEmailSettingsHelpText(emailType: GlobalEmailTypes) {
        return this.TR.key(
            `admin.softwareEmailSettings-helpText-${GlobalEmailTypes[emailType]}`
        );
    }

    // Move to tag maker inside "design" or "team-pizza"
    getPizzaOrientationMapClass(orientation: string) {
        return `pizza-chart-quad--${orientation}`;
    }

    getBehaviorDescription(quadrant: string, isExecutiveAlignment: boolean) {
        return isExecutiveAlignment
            ? this.TR.key(
                  `strategySurvey-results.behaviorDescription-${quadrant}`
              )
            : this.TR.key(
                  `inspireTeam-results.behaviorDescription-${quadrant}`
              );
    }

    getStrategyName(quadrant: string) {
        return this.TR.key(`strategySurvey.${quadrant}`);
    }

    getUpdatedStrategyName(quadrant: string) {
        return this.TR.key(`strategySurvey.${quadrant}.updated`);
    }

    getInterviewQuestion(
        factor: BehavioralFactorNoE,
        personSigmaValue: number,
        jobRange: MinMax,
        isQuestionOneOfTwo: boolean
    ) {
        const number = isQuestionOneOfTwo ? 1 : 2;
        const fitGap =
            personSigmaValue >= jobRange.min && personSigmaValue <= jobRange.max
                ? GuideFactorCategory.Fit
                : GuideFactorCategory.Gap;

        let highLow;

        // in the case of fits, the high/low refers to where they are relative to midpoint,
        // in the case of gaps, the high/low refers to where the job range lies relative to the person
        if (fitGap === GuideFactorCategory.Fit) {
            highLow =
                personSigmaValue > 0
                    ? GuideFactorCondition.High
                    : GuideFactorCondition.Low;
        } else {
            highLow =
                personSigmaValue > jobRange.max
                    ? GuideFactorCondition.Low
                    : GuideFactorCondition.High;
        }

        return `guide.interview.master.${factor.toLowerCase()}.${fitGap}.${highLow}.${number}`;
    }

    getMonth(month: string) {
        return `date.${month}`;
    }

    getTeamConfigName(strategy: string, configType: PizzaConfigType) {
        return `${configType}Config.name-${strategy}`;
    }

    getTeamConfigPageName(strategy: string) {
        return `teamConfig.name-${strategy}.page`;
    }

    getPluralTeamConfigName(teamName: string) {
        return `teamConfig.name-plural-${teamName}`;
    }

    getPluralTeamFunctionNameKey(teamFunctionName: string) {
        return `teamConfig.team-function.plural.${teamFunctionName}`;
    }

    getTeamConfigShortDesc(strategy: string, configType: PizzaConfigType) {
        return `${configType}Config.shortDesc-${strategy}`;
    }
    getTeamConfigDesc(strategy: string, configType: PizzaConfigType) {
        return `${configType}Config.desc-${strategy}`;
    }

    getTeamConfigQuip(strategy: string) {
        return `TeamConfig.quip-${strategy}`;
    }

    getObjectiveSelectorSubtitle(showSelector: boolean) {
        return showSelector
            ? this.TR.key('design-explore-selector.description')
            : this.TR.key('design-strategy-teamType.description');
    }

    getObjectiveTypeName(objectiveType: PizzaObjectiveType) {
        return objectiveType === PizzaObjectiveType.Leadership
            ? this.TR.key('pizzaExplore.leadership.objectives')
            : this.TR.key('pizzaExplore.standard.objectives');
    }

    getObjectiveTypeSwitchDesc(objectiveType: PizzaObjectiveType) {
        return objectiveType === PizzaObjectiveType.Leadership
            ? this.TR.key('pizzaExplore-switchObjectiveType.standard')
            : this.TR.key('pizzaExplore-switchObjectiveType.leadership');
    }

    getFreeJobChooseModalTitle(jobChosen: string) {
        return `freeApp.jobs.${jobChosen}.title`;
    }

    getFreeJobChooseModalDesc(jobChosen: string) {
        return `freeApp.jobs.${jobChosen}.description`;
    }

    getDiagnoseActionTimelineHeader(timelineStep: EngageChecklistCategory) {
        return `engage-actionCenter-category-${EngageChecklistCategory[timelineStep]}`;
    }

    getActionTipTitle(
        derievedTeamname: string,
        identifier: number,
        actionType: TeamDiscoveryActionType,
        actionSubType: TeamDiscoveryActionSubtype
    ) {
        return actionType === 'team'
            ? `${actionType}-${derievedTeamname.toLocaleLowerCase()}-action${identifier}.title`
            : `${actionType}-${derievedTeamname.toLocaleLowerCase()}-${actionSubType}${identifier}.title`;
    }

    getActionTipExtraTitle(behavId: number) {
        return `pizza-right-column.takeaways.title${behavId}`;
    }

    getActionTipDescription(
        derievedTeamname: string,
        identifier: number,
        actionType: 'strategic' | 'team',
        actionSubType: 'behavior' | 'cultural'
    ) {
        return actionType === 'team'
            ? `${actionType}-${derievedTeamname.toLocaleLowerCase()}-action${identifier}.description`
            : `${actionType}-${derievedTeamname.toLocaleLowerCase()}-${actionSubType}${identifier}.description`;
    }

    getDesignBlindspotsOrStrengths(
        configType: PizzaConfigType,
        configResult: string,
        blindspotOrStrength: BlindspotOrStrengthType
    ): Array<string> {
        if (configType === 'team') {
            return [
                this.TR.key(`${configResult}.${blindspotOrStrength}-1`),
                this.TR.key(`${configResult}.${blindspotOrStrength}-2`),
                this.TR.key(`${configResult}.${blindspotOrStrength}-3`),
                // the last one is an insight for blind spot or strength
                this.TR.key(`${configResult}.${blindspotOrStrength}-insight`)
            ];
        } else if (configType === 'strat') {
            return [
                this.TR.key(
                    `design.teamandstratconfig.${configResult}.${blindspotOrStrength}-1`
                ),
                this.TR.key(
                    `design.teamandstratconfig.${configResult}.${blindspotOrStrength}-2`
                ),
                this.TR.key(
                    `design.teamandstratconfig.${configResult}.${blindspotOrStrength}-3`
                )
            ];
        } else {
            return [];
        }
    }

    getDesignStrategyTendencies(strategy: string): Array<string> {
        return [
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-1`),
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-2`),
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-3`),
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-4`),
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-5`),
            this.TR.key(`design.strategy.${strategy.toLowerCase()}.tendency-6`)
        ];
    }

    getDesignConfigIntroKey(configType: PizzaConfigType, configName: string) {
        const aOrAn = this.aOrAn(this.TR.key(configName)[2]);
        return configType === 'team'
            ? `${this.TR.key('design-team-config-intro')} ${aOrAn}`
            : `${this.TR.key('design-strat-config-intro')} ${aOrAn}`;
    }

    getActivityTitle(pageName: string, index: string): string {
        return this.TR.key(`${pageName}-activity-title-${index}`);
    }

    getActivitySubtitle(pageName: string, index: string): string {
        return this.TR.key(`${pageName}-activity-subtitle-${index}`);
    }

    getActivityDesc(pageName: string, index: string): string {
        return this.TR.key(`${pageName}-activity-desc-${index}`);
    }

    aOrAn(text: string) {
        return text.match(`^[aeiouAEIOU].*`)
            ? this.TR.key('common.an')
            : this.TR.key('common.a');
    }

    getObjectivesWarningBody(selectedObjectiveType: PizzaObjectiveType) {
        // displays the objective type opposite of the currently selected type
        let objectiveType: string;
        switch (selectedObjectiveType) {
            case PizzaObjectiveType.Leadership:
                objectiveType = this.TR.key('pizza.standard.team');
                break;
            case PizzaObjectiveType.Standard:
                objectiveType = this.TR.key('pizza.leadership.team');
                break;
            default:
                objectiveType = '';
                break;
        }
        return this.stringUtil.formatString(
            this.TR.key('pizza.objectives-warning.body'),
            objectiveType
        );
    }

    getObjectivesWarningAlert(selectedObjectiveType: PizzaObjectiveType) {
        // displays the objective type of the currently selected type
        let objectiveType: string;
        switch (selectedObjectiveType) {
            case PizzaObjectiveType.Leadership:
                objectiveType = this.TR.key('pizza.leadership.team');
                break;
            case PizzaObjectiveType.Standard:
                objectiveType = this.TR.key('pizza.standard.team');
                break;
            default:
                objectiveType = '';
                break;
        }
        return this.stringUtil.formatString(
            this.TR.key('pizza.objectives-warning.alert'),
            objectiveType
        );
    }

    getTeamDiscoveryConfirmationModalBodyText(
        unusedValueMetrics: number,
        hasUnlimitedTeamSessionsAvailable: boolean
    ) {
        let bodyText = this.TR.key('value-metrics.modal.subtitle.part2');
        if (!hasUnlimitedTeamSessionsAvailable) {
            bodyText = `${this.stringUtil.formatString(
                this.TR.key('value-metrics.modal.subtitle.part1'),
                unusedValueMetrics,
                this.getTeamPluralized(unusedValueMetrics)
            )}<br>${bodyText}`;
        }

        return bodyText;
    }

    getTeamPluralized(teamCount: number) {
        return teamCount === 1
            ? this.TR.key('common.team').toLowerCase()
            : this.TR.key('label.common-terms.teams').toLowerCase();
    }

    getTeamTypeInsightDescription(percent: number, teamName: string) {
        const byteDesc: string = this.TR.key(
            'teamConfig.teamtype-analytic-byte'
        );

        const name: string = this.TR.key(teamName);

        return this.stringUtil.formatString(byteDesc, percent + '%', name);
    }

    getTeamTypeByTeamFunctionInsightDescription(
        percent: number,
        teamNameKey: string,
        teamFunctionKey: string
    ) {
        const teamTypeByTeamFunctionInsightDesc: string = this.TR.key(
            'teamConfig.teamtype-teamfunction-insight'
        );

        const teamName: string = this.TR.key(teamNameKey);
        const teamFunction: string = this.TR.key(teamFunctionKey);

        return this.stringUtil.formatString(
            teamTypeByTeamFunctionInsightDesc,
            percent + '%',
            teamFunction,
            teamName
        );
    }

    getTeamSizeByTeamFunctionInsightDescription(
        size: number,
        teamFunctionKey: string
    ) {
        const teamSizeByTeamFunctionInsightDesc: string = this.TR.key(
            'teamConfig.workstyle.teamsize-by-teamfunction.insight'
        );

        const teamFunction: string = this.TR.key(teamFunctionKey);

        return this.stringUtil.formatString(
            teamSizeByTeamFunctionInsightDesc,
            teamFunction,
            size
        );
    }

    getAverageTeamSizeByTeamFunctionInsightDescription(size: number) {
        const averageTeamSizeInsightDesc: string = this.TR.key(
            'teamConfig.workstyle.teamsize-average.insight'
        );

        return this.stringUtil.formatString(averageTeamSizeInsightDesc, size);
    }

    getBuiltInAverageTeamSizeInsightDescription() {
        return this.TR.key('teamConfig.workstyle-average.teamsize-insight');
    }

    getTeamStrategyTypeByTeamTypeInsightDescription(
        percent: number,
        teamTypeNameKey: string
    ) {
        const teamStrategyTypeByTeamTypeInsightDesc: string = this.TR.key(
            'teamConfig.team-strategy.teamtype.frequency-insight'
        );

        const name: string = this.TR.key(teamTypeNameKey);

        return this.stringUtil.formatString(
            teamStrategyTypeByTeamTypeInsightDesc,
            percent + '%',
            name
        );
    }

    getTeamStrategyFrequencyInsightDescription(strategyType: DerivedTeamName) {
        const teamStrategyFreqDesc: string = this.TR.key(
            'teamConfig.team-strategy.frequency-insight'
        );

        const percent: number =
            derivedTeamStrategyPercentageMap.get(strategyType);

        return this.stringUtil.formatString(
            teamStrategyFreqDesc,
            percent + '%'
        );
    }

    getTeamIndividualContributionInsightDescription(
        contributionType: TeamIndividualContributionType
    ) {
        return this.TR.key(
            `teamConfig.team-individual.${contributionType.toString()}.contribution`
        );
    }

    getManagerInsightDescription(managerType: TeamManagerType) {
        return this.TR.key(
            `teaminsights.manager-strength.${managerType.toString()}`
        );
    }

    //* ****Start of HIRE specific keys*****//

    // Allows Recent Job Activity dashboard card job target reference profile name in tooltip to appear in non-English languages
    getJobMatchingReferenceProfileTooltip(name: RefProfileReportInfo) {
        return this.TR.key(`toolkit-referenceProfile.name-${name}`);
    }

    /**
     * Allows Recent Job Activity dashboard card job target matching reference
     * profile description in tooltip to appear in non-English languages
     */
    getJobMatchingReferenceProfileDescriptionTooltip(
        name: RefProfileReportInfo
    ) {
        return this.TR.key(`toolkit-referenceProfile.tooltip-${name}`);
    }

    getBenchmarkRefinmentQuestion(factor: string) {
        return this.TR.key(
            `benchmarkRefinmentComponent.question.${factor.toLowerCase()}.1`
        );
    }

    getHireIndustry(industryNumber: number) {
        return this.TR.key(`hire.jobIndustry.${industryNumber}`);
    }

    //* ****End of HIRE specific keys*****//
}
